/**
 * PSFS (Patient-Specific Functional Scale)
 * Escala Funcional Específica del Paciente
 * 
 * DESCRIPCIÓN:
 * Cuestionario validado para medir la función de forma personalizada.
 * El paciente define 3-5 actividades importantes que están afectadas
 * por su problema y puntúa su capacidad actual (0-10).
 * 
 * USO CLÍNICO:
 * - Se usa al INICIO del episodio para establecer baseline
 * - En revisiones (6 meses / 12 meses) se RE-PUNTÚAN las mismas actividades
 * - Permite ver evolución real de las actividades específicas del paciente
 * 
 * PUNTUACIÓN:
 * - 0 = "No puedo hacerlo en absoluto"
 * - 10 = "Lo hago como antes / sin ninguna dificultad"
 * - Mean score = media de todas las actividades
 * - Un cambio de 2+ puntos se considera clínicamente significativo (MCID)
 */

// Configuración del PSFS
export const PSFS_CONFIG = {
  // Versión del cuestionario
  version: 'PSFS',
  
  // Número de actividades
  minActivities: 3,
  maxActivities: 5,
  defaultActivities: 3,
  
  // Rango de puntuación
  minScore: 0,
  maxScore: 10,
  
  // Si permite decimales (por defecto enteros)
  allowDecimals: false,
  
  // MCID (Minimum Clinically Important Difference)
  mcid: 2,
  
  // Días para advertir de cuestionario reciente
  recentDaysWarning: 30,
  
  // Textos
  texts: {
    title: 'PSFS (Escala Funcional Específica)',
    fullTitle: 'Patient-Specific Functional Scale',
    instructions: 'Piense en 3 actividades importantes que no puede realizar o le resultan difíciles debido a su problema actual. Luego, puntúe de 0 a 10 su capacidad para realizar cada actividad HOY.',
    instructionsFollowup: 'A continuación se muestran las actividades que definió anteriormente. Por favor, puntúe su capacidad actual para realizar cada una de ellas.',
    activityPlaceholder: 'Ej: Subir escaleras, Levantar objetos pesados, Caminar 30 minutos...',
    scoreMin: 'No puedo hacerlo',
    scoreMax: 'Sin dificultad',
    addActivity: 'Añadir otra actividad',
    editActivitiesWarning: 'Si modifica las actividades, perderá la comparabilidad con evaluaciones anteriores. ¿Está seguro?'
  }
};

// Schema para el sistema de cuestionarios
export const PSFS_SCHEMA = {
  id: 'psfs',
  name: 'PSFS (Escala Funcional Específica)',
  fullName: 'Patient-Specific Functional Scale',
  description: 'Cuestionario personalizado donde el paciente define sus actividades afectadas y puntúa su capacidad. Ideal para seguimiento de evolución funcional.',
  estimatedTime: '3-5 minutos',
  category: 'funcional',
  version: 'PSFS',
  requiresLicense: false,
  isPSFS: true, // Flag para detección especial
  targetPopulation: 'Cualquier paciente con limitación funcional',
  scoring: {
    type: 'mean',
    method: 'average',
    range: { min: 0, max: 10 },
    mcid: 2,
    interpretation: {
      0: 'Incapacidad total',
      2: 'Limitación severa',
      4: 'Limitación moderada-severa',
      6: 'Limitación moderada',
      8: 'Limitación leve',
      10: 'Sin limitación'
    }
  },
  sections: [
    {
      id: 'psfs_activities',
      title: 'Actividades funcionales',
      questions: [] // Dinámico según las actividades del paciente
    }
  ]
};

/**
 * Calcula las puntuaciones del PSFS
 * @param {Array} activities - Array de { label: string, score_0_10: number }
 * @returns {Object} Resultados calculados
 */
export const calculatePSFSScore = (activities) => {
  const results = {
    mean_score: null,
    total_score: null,
    activities_count: 0,
    valid_activities: [],
    missing_scores_count: 0,
    interpretation: null,
    change_from_baseline: null
  };

  if (!activities || !Array.isArray(activities)) {
    return results;
  }

  // Filtrar actividades válidas (con label y score)
  const validActivities = activities.filter(a => 
    a && 
    a.label && 
    a.label.trim() !== '' && 
    a.score_0_10 !== undefined && 
    a.score_0_10 !== null
  );

  results.activities_count = validActivities.length;
  results.valid_activities = validActivities;

  // Contar actividades sin puntuación
  const activitiesWithLabel = activities.filter(a => a && a.label && a.label.trim() !== '');
  results.missing_scores_count = activitiesWithLabel.length - validActivities.length;

  if (validActivities.length > 0) {
    // Calcular suma y media
    const scores = validActivities.map(a => a.score_0_10);
    const sum = scores.reduce((a, b) => a + b, 0);
    const mean = sum / scores.length;

    results.total_score = Math.round(sum * 10) / 10;
    results.mean_score = Math.round(mean * 10) / 10;

    // Interpretación
    if (mean >= 9) {
      results.interpretation = 'Sin limitación funcional';
    } else if (mean >= 7) {
      results.interpretation = 'Limitación leve';
    } else if (mean >= 5) {
      results.interpretation = 'Limitación moderada';
    } else if (mean >= 3) {
      results.interpretation = 'Limitación moderada-severa';
    } else {
      results.interpretation = 'Limitación severa';
    }
  }

  return results;
};

/**
 * Calcula el cambio desde el baseline
 * @param {number} currentMean - Mean score actual
 * @param {number} baselineMean - Mean score del baseline
 * @returns {Object} Información sobre el cambio
 */
export const calculatePSFSChange = (currentMean, baselineMean) => {
  if (currentMean === null || baselineMean === null) {
    return { change: null, isSignificant: false, direction: null };
  }

  const change = Math.round((currentMean - baselineMean) * 10) / 10;
  const isSignificant = Math.abs(change) >= PSFS_CONFIG.mcid;
  
  let direction = 'sin cambio';
  if (change > 0) {
    direction = 'mejoría';
  } else if (change < 0) {
    direction = 'empeoramiento';
  }

  return {
    change,
    isSignificant,
    direction,
    description: isSignificant 
      ? `Cambio clínicamente significativo (${change > 0 ? '+' : ''}${change} puntos)` 
      : `Cambio no significativo (${change > 0 ? '+' : ''}${change} puntos)`
  };
};

/**
 * Valida las actividades del PSFS
 * @param {Array} activities - Array de actividades
 * @param {Object} config - Configuración
 * @returns {Object} { isValid, errors, warnings }
 */
export const validatePSFSActivities = (activities, config = PSFS_CONFIG) => {
  const errors = [];
  const warnings = [];

  if (!activities || !Array.isArray(activities)) {
    errors.push('No se han definido actividades');
    return { isValid: false, errors, warnings };
  }

  // Filtrar actividades con label
  const activitiesWithLabel = activities.filter(a => a && a.label && a.label.trim() !== '');

  // Verificar número mínimo de actividades
  if (activitiesWithLabel.length < config.minActivities) {
    errors.push(`Debe definir al menos ${config.minActivities} actividades`);
  }

  // Verificar puntuaciones
  activitiesWithLabel.forEach((activity, index) => {
    if (activity.score_0_10 === undefined || activity.score_0_10 === null) {
      errors.push(`La actividad "${activity.label}" necesita una puntuación`);
    } else if (activity.score_0_10 < config.minScore || activity.score_0_10 > config.maxScore) {
      errors.push(`La puntuación de "${activity.label}" debe estar entre ${config.minScore} y ${config.maxScore}`);
    }
  });

  // Verificar si hay actividades vacías intermedias
  activities.forEach((activity, index) => {
    if (activity && (!activity.label || activity.label.trim() === '') && activity.score_0_10 !== undefined) {
      warnings.push(`La actividad ${index + 1} tiene puntuación pero no tiene nombre`);
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    activitiesCount: activitiesWithLabel.length
  };
};

/**
 * Obtiene el color según la puntuación
 */
export const getPSFSScoreColor = (score) => {
  if (score === null || score === undefined) return '#6B7280';
  if (score >= 8) return '#10B981'; // green
  if (score >= 6) return '#84CC16'; // lime
  if (score >= 4) return '#EAB308'; // yellow
  if (score >= 2) return '#F97316'; // orange
  return '#EF4444'; // red
};

/**
 * Formatea el score para mostrar
 */
export const formatPSFSScore = (score) => {
  if (score === null || score === undefined) return '—';
  return score.toFixed(1);
};

export default PSFS_SCHEMA;
